import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import SuperAdminLayout from './SuperAdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://dare.exlva.com';

// Default footer settings
const DEFAULT_SETTINGS = {
  copyright_text: '© {year} BioQuest Solutions. All rights reserved.',
  powered_by_text: 'Powered by BioQuest Solutions',
  powered_by_url: 'https://www.bioquestglobal.com',
  is_visible: true,
  background_color: '#111827',
  text_color: '#9CA3AF',
  link_color: '#60A5FA',
  font_size: 'small'
};

const FooterSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });

  useEffect(() => {
    // BUG FIX: Use correct localStorage key 'exlva_dare_user' instead of 'user'
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    
    // Allow both super_admin and admin roles
    if (!user.id || !['super_admin', 'admin'].includes(user.role)) {
      navigate('/login');
      return;
    }
    
    fetchFooterSettings();
  }, [navigate]);

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/footer-settings`);
      if (response.data) {
        // Merge with defaults to ensure all fields are present
        setSettings({
          ...DEFAULT_SETTINGS,
          ...response.data
        });
      }
    } catch (error) {
      console.log('Using default footer settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // BUG FIX: Use correct localStorage key
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id) {
      setError('Please log in again');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(
        `${API_URL}/api/admin/footer-settings?user_id=${user.id}`,
        settings
      );
      
      if (response.data.success) {
        setSuccess('Footer settings saved successfully!');
        if (response.data.settings) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...response.data.settings
          });
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save footer settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id) {
      setError('Please log in again');
      return;
    }

    setResetting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(
        `${API_URL}/api/admin/footer-settings?user_id=${user.id}`,
        DEFAULT_SETTINGS
      );
      
      if (response.data.success) {
        setSettings({ ...DEFAULT_SETTINGS });
        setSuccess('Footer settings reset to defaults!');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to reset footer settings');
    } finally {
      setResetting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  // Get font size in pixels for preview
  const getFontSizeClass = (size) => {
    switch (size) {
      case 'small': return 'text-sm';
      case 'medium': return 'text-base';
      case 'large': return 'text-lg';
      default: return 'text-sm';
    }
  };

  // Preview with current year
  const currentYear = new Date().getFullYear();
  const previewCopyright = (settings.copyright_text || '').replace('{year}', currentYear);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Footer Settings</h1>
            <p className="mt-1 text-sm text-gray-600">
              Customize the application footer that appears on all pages.
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={resetting || saving}
            className="text-gray-600"
          >
            {resetting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset to Default
              </>
            )}
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {/* Footer Content Card */}
        <Card>
          <CardHeader>
            <CardTitle>Footer Content</CardTitle>
            <CardDescription>
              Configure the text and links displayed in the application footer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Copyright Text */}
            <div className="space-y-2">
              <Label htmlFor="copyright_text">Copyright Text</Label>
              <Input
                id="copyright_text"
                value={settings.copyright_text}
                onChange={(e) => handleInputChange('copyright_text', e.target.value)}
                placeholder="© {year} Company Name. All rights reserved."
              />
              <p className="text-sm text-gray-500">
                Use <code className="bg-gray-100 px-1 rounded">{'{year}'}</code> to insert the current year dynamically.
              </p>
            </div>

            {/* Powered By Text */}
            <div className="space-y-2">
              <Label htmlFor="powered_by_text">Powered By Text</Label>
              <Input
                id="powered_by_text"
                value={settings.powered_by_text}
                onChange={(e) => handleInputChange('powered_by_text', e.target.value)}
                placeholder="Powered by Company Name"
              />
            </div>

            {/* Powered By URL */}
            <div className="space-y-2">
              <Label htmlFor="powered_by_url">Powered By Link URL</Label>
              <Input
                id="powered_by_url"
                value={settings.powered_by_url}
                onChange={(e) => handleInputChange('powered_by_url', e.target.value)}
                placeholder="https://www.example.com"
                type="url"
              />
            </div>

            {/* Visibility Toggle */}
            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label htmlFor="is_visible">Show Footer</Label>
                <p className="text-sm text-gray-500">Toggle to show or hide the footer on all pages</p>
              </div>
              <Switch
                id="is_visible"
                checked={settings.is_visible}
                onCheckedChange={(checked) => handleInputChange('is_visible', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Appearance Card */}
        <Card>
          <CardHeader>
            <CardTitle>Footer Appearance</CardTitle>
            <CardDescription>
              Customize the visual styling of the footer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Color Controls Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Background Color */}
              <div className="space-y-2">
                <Label htmlFor="background_color">Background Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="background_color_picker"
                    value={settings.background_color}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    id="background_color"
                    value={settings.background_color}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    placeholder="#111827"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Text Color */}
              <div className="space-y-2">
                <Label htmlFor="text_color">Text Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="text_color_picker"
                    value={settings.text_color}
                    onChange={(e) => handleInputChange('text_color', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    id="text_color"
                    value={settings.text_color}
                    onChange={(e) => handleInputChange('text_color', e.target.value)}
                    placeholder="#9CA3AF"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Link Color */}
              <div className="space-y-2">
                <Label htmlFor="link_color">Link Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="link_color_picker"
                    value={settings.link_color}
                    onChange={(e) => handleInputChange('link_color', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    id="link_color"
                    value={settings.link_color}
                    onChange={(e) => handleInputChange('link_color', e.target.value)}
                    placeholder="#60A5FA"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Font Size */}
            <div className="space-y-2">
              <Label htmlFor="font_size">Font Size</Label>
              <div className="flex gap-4">
                {['small', 'medium', 'large'].map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleInputChange('font_size', size)}
                    className={`px-4 py-2 rounded-lg border transition-colors capitalize ${
                      settings.font_size === size
                        ? 'bg-blue-600 text-white border-sky-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500">
                Choose the font size for footer text.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              This is how your footer will appear on the application.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className={`py-4 rounded-lg ${getFontSizeClass(settings.font_size)}`}
              style={{ backgroundColor: settings.background_color }}
            >
              <div className="px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
                <p style={{ color: settings.text_color }}>
                  {previewCopyright}
                </p>
                <a 
                  href={settings.powered_by_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  style={{ color: settings.link_color }}
                >
                  {settings.powered_by_text}
                </a>
              </div>
            </div>
            {!settings.is_visible && (
              <p className="mt-2 text-sm text-amber-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Footer is currently hidden
              </p>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button 
            onClick={handleSave} 
            disabled={saving || resetting}
            className="px-6"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default FooterSettings;
